import { useLang } from "@/lib/LanguageContext";
import { heroImage } from "@/lib/portfolio";

/** Stagger config — sequential 90ms gaps for a piano-key cadence. */
const STEP = 0.09;
const start = (n) => ({ animationDelay: `${n * STEP}s` });

function Word({ text, delay, italic, color }) {
    return (
        <span
            className={`piano-key piano-inline ${italic ? "italic " : ""}${color || ""}`}
            style={start(delay)}
        >
            {text}
            <span className="inline-block w-[0.28em]" />
        </span>
    );
}

export default function Hero() {
    const { t } = useLang();

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const line1Words = (t.hero.line1 || "").split(" ").filter(Boolean);
    const line2Words = (t.hero.line2Italic || "").split(" ").filter(Boolean);
    const line3Words = (t.hero.line3 || "").split(" ").filter(Boolean);
    const line4Words = (t.hero.line4Italic || "").split(" ").filter(Boolean);

    let i = 0; // monotonically increasing piano-key index

    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative pt-28 md:pt-36 pb-20 md:pb-28"
        >
            <div className="mx-auto max-w-screen-2xl px-6 md:px-12 lg:px-20">
                <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
                    {/* Left: typographic statement */}
                    <div className="col-span-12 md:col-span-7">
                        <p
                            className="overline text-navy/60 mb-8 piano-key"
                            data-testid="hero-kicker"
                            style={start(i++)}
                        >
                            <span className="inline-block w-10 h-px align-middle bg-terracotta mr-3" />
                            {t.hero.kicker}
                        </p>
                        <h1
                            data-testid="hero-headline"
                            className="font-serif text-[3rem] leading-[0.95] sm:text-[4.5rem] lg:text-[6.5rem] xl:text-[7.5rem] font-light tracking-tight text-navy"
                        >
                            {line1Words.map((w) => (
                                <Word
                                    key={`l1-${w}-${i}`}
                                    text={w}
                                    delay={i++}
                                    color="text-ultramarine"
                                />
                            ))}
                            {line2Words.length > 0 && (
                                <>
                                    <br />
                                    {line2Words.map((w) => (
                                        <Word
                                            key={`l2-${w}-${i}`}
                                            text={w}
                                            delay={i++}
                                            italic
                                            color="text-terracotta"
                                        />
                                    ))}
                                </>
                            )}
                            {line3Words.length > 0 && (
                                <>
                                    <br />
                                    {line3Words.map((w) => (
                                        <Word key={`l3-${w}-${i}`} text={w} delay={i++} />
                                    ))}
                                </>
                            )}
                            {line4Words.length > 0 && (
                                <>
                                    {line4Words.map((w) => (
                                        <Word
                                            key={`l4-${w}-${i}`}
                                            text={w}
                                            delay={i++}
                                            italic
                                            color="text-teal"
                                        />
                                    ))}
                                </>
                            )}
                        </h1>

                        <div className="mt-12 max-w-xl">
                            <p
                                className="piano-key text-base sm:text-lg leading-relaxed text-navy/80"
                                style={start(i++)}
                            >
                                {t.hero.subhead}
                            </p>
                            <p
                                className="piano-key mt-5 text-sm leading-relaxed text-navy/55"
                                style={start(i++)}
                            >
                                {t.hero.intro}
                            </p>
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <button
                                type="button"
                                data-testid="hero-cta-primary"
                                onClick={() => scrollTo("work")}
                                className="btn-primary piano-key"
                                style={start(i++)}
                            >
                                {t.hero.ctaPrimary} →
                            </button>
                            <button
                                type="button"
                                data-testid="hero-cta-secondary"
                                onClick={() => scrollTo("contact")}
                                className="btn-outline piano-key"
                                style={start(i++)}
                            >
                                {t.hero.ctaSecondary}
                            </button>
                        </div>

                        {/* trust chips */}
                        <ul
                            data-testid="hero-trust-chips"
                            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3"
                        >
                            {t.hero.chips.map((c, idx) => (
                                <li
                                    key={c}
                                    className="overline text-navy/55 flex items-center gap-3 piano-key"
                                    style={start(i++)}
                                >
                                    {idx > 0 && (
                                        <span className="hidden md:inline-block w-1 h-1 rounded-full bg-navy/30" />
                                    )}
                                    {c}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Right: framed editorial image */}
                    <div className="col-span-12 md:col-span-5 relative">
                        <div
                            className="img-hover relative aspect-[3/4] w-full bg-ivory-deep piano-key"
                            style={start(i++)}
                        >
                            <img
                                src={heroImage}
                                alt={`Expressive painting by ${t.brand.name}`}
                                className="absolute inset-0 h-full w-full object-cover"
                                loading="eager"
                                data-testid="hero-image"
                            />
                        </div>
                        <div className="flex items-start justify-between mt-4">
                            <p
                                className="overline text-navy/50 piano-key"
                                style={start(i++)}
                            >
                                Pl. 01 / Studio archive
                            </p>
                            <p
                                className="overline text-navy/50 piano-key"
                                style={start(i++)}
                            >
                                Hand-painted, 2025
                            </p>
                        </div>

                        {/* vertical tag */}
                        <p
                            className="hidden md:block absolute -left-6 top-2 vertical-rl overline text-navy/40 piano-key"
                            style={start(i++)}
                        >
                            {t.hero.scroll}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
